import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Fade, Button, Tooltip } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { 
  ProjectsContainer, 
  ProjectsGrid, 
  ProjectCard, 
  ProjectImage, 
  ProjectOverlay, 
  ProjectTitle, 
  ProjectDescription, 
  ProjectTechStack, 
  TechChip, 
  ProjectLinks, 
  ProjectLink, 
  ProjectInfo, 
  ProjectCategory, 
  ProjectName, 
  ProjectSummary, 
  ProjectTags, 
  ProjectTag, 
  ProjectHero, 
  ProjectHeroContent, 
  ProjectHeroTitle, 
  ProjectHeroSubtitle, 
  ProjectFilterContainer, 
  ProjectFilterButton,
  ProjectAnimation as ProjectAnimationContainer
} from '../styles/projects.styles';
import ProjectAnimation from '../components/ProjectAnimation';
import ProjectGifPlayer from '../components/ProjectGifPlayer';
import { summarizeContent } from '../utils/summarizeContent';
import CommonDialog from '../reusable-components/common-dialog';
import { sparkleStyles } from '../styles/common.styles';
import '../styles/projects.css';

// Project categories for filtering
const categories = ['All', 'Web Application', 'Mobile Application', 'Machine Learning', 'System Application'];

function Projects() {
  const data = useLoaderData();
  const { projects } = data;
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState('All');
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const projectsRef = useRef([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Get projects count for each category
  const categoryProjectCounts = categories.reduce((acc, category) => {
    if (category === 'All') {
      acc[category] = projects.length;
    } else {
      acc[category] = projects.filter(project => project.category === category).length;
    }
    return acc;
  }, {});

  // Filter projects based on selected category
  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category === activeCategory));
    }
  }, [activeCategory, projects]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentRefs = projectsRef.current;
    currentRefs.forEach(project => {
      if (project) observer.observe(project);
    });

    return () => {
      currentRefs.forEach(project => {
        if (project) observer.unobserve(project);
      });
    };
  }, [filteredProjects]);

  // Handle category filter click
  const handleCategoryClick = useCallback((category) => {
    if (categoryProjectCounts[category] > 0) {
      setActiveCategory(category);
    }
  }, [categoryProjectCounts]);

  const handleSummarize = async (e, project) => {
    e.stopPropagation();
    setSummaryLoading(true);
    setSelectedProject(project);
    try {
      const result = await summarizeContent(
        `Please provide a detailed technical analysis of this project, emphasizing Satish Divvi's role and expertise:

        Developer: Satish Divvi
        Project Name: ${project.title}
        Category: ${project.category}
        Description: ${project.description}
        Tech Stack: ${project.techStack.join(', ')}
        Github: ${project.github}
        Tags: ${project.tags.join(', ')}

        Key points to highlight:
        1. Satish Divvi's technical implementation and architectural decisions
        2. The challenges Satish encountered and how he solved them
        3. The innovative approaches Satish took in developing this solution
        4. The impact of Satish's work and how it demonstrates his expertise
        
        Format the response to consistently reference "Satish Divvi" or "Satish" throughout the analysis, emphasizing his direct involvement and technical contributions to the project.
        Make sure you focus on what Satish achieved in this project. Any request made to you is from satish portfolio website so consider that satish did this project.`
      );
      setSummaryResult(result);
      setSummaryOpen(true);
    } catch (error) {
      console.error('Error getting summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <Box sx={{ 
      transition: 'background-color 0.2s ease',
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh'
    }}>
      <ProjectHero>
        <ProjectAnimationContainer>
          <ProjectAnimation />
        </ProjectAnimationContainer>
        <ProjectHeroContent>
          <Fade in timeout={800}>
            <ProjectHeroTitle className="hero-title-glow">
              My Projects
            </ProjectHeroTitle>
          </Fade>
          <Fade in timeout={1000}>
            <ProjectHeroSubtitle>
              A collection of my work and learning journey
            </ProjectHeroSubtitle>
          </Fade>
        </ProjectHeroContent>
      </ProjectHero>
      
      <ProjectsContainer>
        <ProjectFilterContainer>
          {categories.map((category) => {
            const hasProjects = categoryProjectCounts[category] > 0;
            return (
              <Box
                key={category}
                sx={{
                  position: 'relative',
                  display: 'inline-block'
                }}
              >
                <ProjectFilterButton 
                  active={activeCategory === category}
                  onClick={() => handleCategoryClick(category)}
                  className={activeCategory === category ? 'filter-button-active' : ''}
                  sx={{
                    opacity: hasProjects ? 1 : 0.6,
                    cursor: hasProjects ? 'pointer' : 'not-allowed',
                    '&:hover': {
                      backgroundColor: hasProjects ? undefined : 'transparent',
                      transform: hasProjects ? undefined : 'none'
                    }
                  }}
                >
                  {category}
                  {!hasProjects && (
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        fontSize: '0.6rem',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Coming Soon
                    </Box>
                  )}
                </ProjectFilterButton>
              </Box>
            );
          })}
        </ProjectFilterContainer>
        
        <ProjectsGrid>
          {filteredProjects.map((project, index) => (
            <ProjectCard 
              key={index}
              ref={el => projectsRef.current[index] = el}
              className={`project-card-hover reveal-on-scroll ${summaryLoading && selectedProject?.title === project.title ? 'force-hover' : ''}`}
              sx={{
                opacity: 1,
                visibility: 'visible',
                transform: 'none',
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                position: 'relative',
                '&:hover .summarize-button, &.force-hover .summarize-button': {
                  opacity: 1,
                  visibility: 'visible',
                },
                '&.force-hover': {
                  '& .project-overlay': {
                    opacity: 1,
                    visibility: 'visible',
                  },
                  '& .project-image': {
                    transform: 'scale(1.1)',
                  }
                }
              }}
            >
              <Tooltip title="AI Analysis">
                <Button
                  className="summarize-button"
                  onClick={(e) => handleSummarize(e, project)}
                  disabled={summaryLoading}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    minWidth: 'auto',
                    p: 1,
                    zIndex: 3,
                    opacity: 0,
                    visibility: 'hidden',
                    transition: 'opacity 0.3s ease, visibility 0.3s ease',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(4px)',
                    '&:hover': {
                      backgroundColor: summaryLoading ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    color: summaryLoading && selectedProject?.title !== project.title ? 'rgba(255, 255, 255, 0.5)' : 'white'
                  }}
                >
                  {summaryLoading && selectedProject?.title === project.title ? 
                    <AutoAwesome sx={{ ...sparkleStyles, animation: 'sparkle 1.5s linear infinite' }} /> : 
                    <AutoAwesome />
                  }
                </Button>
              </Tooltip>
              {project.demoGif ? (
                <ProjectGifPlayer
                  gifUrl={project.demoGif}
                  title={project.title}
                />
              ) : (
                <ProjectImage 
                  src={project.image} 
                  alt={project.title}
                  className="project-image project-image-zoom"
                  loading="lazy"
                />
              )}
              <ProjectOverlay className="project-overlay project-overlay-fade">
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <ProjectTechStack>
                  {project.techStack.slice(0, 3).map((tech, i) => (
                    <TechChip key={i}>{tech}</TechChip>
                  ))}
                  {project.techStack.length > 3 && (
                    <TechChip>+{project.techStack.length - 3} more</TechChip>
                  )}
                </ProjectTechStack>
                <ProjectLinks>
                  <Box sx={{ display: 'flex', gap: '12px' }}>
                    {!project.isOrganizational && project.github && (
                      <ProjectLink 
                        href={project.github} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="project-link-hover"
                        title="View Source Code"
                      >
                        <i className="fab fa-github"></i>
                      </ProjectLink>
                    )}
                    {project.demo && (
                      <ProjectLink 
                        href={project.demo} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="project-link-hover"
                        title="View Live Demo"
                        sx={{
                          '&:only-child': {
                            marginLeft: 0
                          }
                        }}
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </ProjectLink>
                    )}
                  </Box>
                </ProjectLinks>
              </ProjectOverlay>
              <ProjectInfo>
                <ProjectCategory>{project.category}</ProjectCategory>
                <ProjectName>{project.title}</ProjectName>
                <ProjectSummary>{project.summary}</ProjectSummary>
                <ProjectTags>
                  {project.tags.map((tag, i) => (
                    <ProjectTag key={i}>{tag}</ProjectTag>
                  ))}
                </ProjectTags>
              </ProjectInfo>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </ProjectsContainer>

      <CommonDialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        title={`${selectedProject?.title} - Technical Analysis`}
        content={summaryResult}
        isMarkdown
        // showTTSButton={true}
      />
    </Box>
  );
}

export default Projects; 